import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';

const SHIPPING_FIELDS = [
    'mode',
    'methods',
    'rates',
    'originCountry',
    'originZIPCode',
    'weight',
    'weightUnit',
    'length',
    'width',
    'height',
    'packagePreset',
    'pickupAddress',
    'allowCombinedShipping',
    'secondaryRate',
];

const EXAMPLE_REFRESH_FIELDS = new Set([
    'methods',
    'originZIPCode',
    'weight',
    'length',
    'width',
    'height',
]);

const DEFAULT_STATE = {
    mode: null,
    methods: [],
    rates: {},
    originCountry: '',
    originZIPCode: '',
    weight: '',
    weightUnit: 'lbs',
    length: '',
    width: '',
    height: '',
    packagePreset: '',
    pickupAddress: '',
    allowCombinedShipping: false,
    secondaryRate: '',
};

const normalizeCountry = value => String(value || '').trim().toUpperCase();

const normalizeState = (shipping = {}, fallbackOriginCountry = 'US') => ({
    ...DEFAULT_STATE,
    ...shipping,
    mode: shipping.mode ?? null,
    methods: Array.isArray(shipping.methods) ? shipping.methods : [],
    rates: shipping.rates && typeof shipping.rates === 'object' ? shipping.rates : {},
    originCountry: normalizeCountry(fallbackOriginCountry)
        || normalizeCountry(shipping.originCountry)
        || 'US',
    weightUnit: shipping.weightUnit || 'lbs',
    allowCombinedShipping: shipping.allowCombinedShipping === true,
});

const normalizeComparableValue = (value) => {
    if (value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') return null;
        const numericValue = Number(trimmed);
        if (!Number.isNaN(numericValue) && `${numericValue}` === trimmed) {
            return numericValue;
        }
        return trimmed;
    }
    if (Array.isArray(value)) {
        return [...value].sort();
    }
    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((acc, key) => {
            acc[key] = normalizeComparableValue(value[key]);
            return acc;
        }, {});
    }
    return value;
};

const valuesMatch = (a, b) => (
    JSON.stringify(normalizeComparableValue(a)) === JSON.stringify(normalizeComparableValue(b))
);

export default function useShippingSettings(productUuid, originCountry = null) {
    const { fetch, isLoading, API_URL } = useAuth();
    const [state, setState] = useState(() => normalizeState({}, originCountry));
    const [savedState, setSavedState] = useState(DEFAULT_STATE);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [examples, setExamples] = useState([]);
    const [examplesLoading, setExamplesLoading] = useState(false);
    const [examplesError, setExamplesError] = useState('');
    const [shippingDefaults, setShippingDefaults] = useState(null);
    const [shippingDefaultsLoading, setShippingDefaultsLoading] = useState(false);
    const [shippingDefaultsError, setShippingDefaultsError] = useState('');
    const [isFirstTime, setIsFirstTime] = useState(false);
    const stateRef = useRef(DEFAULT_STATE);
    const savedStateRef = useRef(DEFAULT_STATE);

    const productUrl = useMemo(() => (
        `${API_URL}/v1/seller/products/${productUuid}`
    ), [API_URL, productUuid]);

    const shippingDefaultsUrl = useMemo(() => (
        `${API_URL}/v1/seller/products/shipping-defaults`
    ), [API_URL]);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        savedStateRef.current = savedState;
    }, [savedState]);

    useEffect(() => {
        if (isLoading || !productUuid) return;

        let active = true;
        setLoading(true);
        setError('');
        setShippingDefaults(null);
        setShippingDefaultsError('');

        fetch(productUrl)
            .then(async (res) => {
                const json = await res.json();
                if (!res.ok) {
                    throw new Error(json.error || json.message || 'Failed to load product');
                }
                return json;
            })
            .then((json) => {
                if (!active) return;
                const storedShipping = json.data?.shipping || {};
                const shipping = normalizeState(storedShipping, originCountry);
                setState(shipping);
                setSavedState(shipping);
                setExamples(Array.isArray(json.data?.shipping?.examples) ? json.data.shipping.examples : []);
                setIsFirstTime(!json.data?.shipping?.mode && !json.data?.shipping?.originZIPCode);
            })
            .catch((e) => {
                if (!active) return;
                setError(e.message || 'Failed to load product');
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [fetch, isLoading, originCountry, productUrl, productUuid]);

    const setField = useCallback((key, value) => {
        setState((current) => ({ ...current, [key]: value }));
    }, []);

    const setFields = useCallback((values) => {
        setState((current) => ({ ...current, ...values }));
    }, []);

    const fetchExamples = useCallback(async () => {
        if (!productUuid) return [];

        setExamplesLoading(true);
        setExamplesError('');

        try {
            const res = await fetch(`${productUrl}/shipping/examples`);
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json.error || json.message || 'Failed to fetch shipping examples');
            }
            const nextExamples = Array.isArray(json.data) ? json.data : [];
            setExamples(nextExamples);
            return nextExamples;
        } catch (e) {
            setExamplesError(e.message || 'Failed to fetch shipping examples');
            return [];
        } finally {
            setExamplesLoading(false);
        }
    }, [fetch, productUrl, productUuid]);

    const fetchShippingDefaults = useCallback(async () => {
        if (!productUuid) return null;

        setShippingDefaultsLoading(true);
        setShippingDefaultsError('');

        try {
            const res = await fetch(shippingDefaultsUrl);
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json.error || json.message || 'Failed to fetch shipping defaults');
            }

            if (!json.data) {
                setShippingDefaults(null);
                return null;
            }

            const { sourceProduct, ...defaults } = json.data;
            const normalizedDefaults = normalizeState(defaults, originCountry);
            const nextDefaults = {
                defaults: SHIPPING_FIELDS.reduce((acc, field) => {
                    if (Object.prototype.hasOwnProperty.call(defaults, field)) {
                        acc[field] = normalizedDefaults[field];
                    }
                    return acc;
                }, {}),
                sourceProduct: sourceProduct || null,
            };
            setShippingDefaults(nextDefaults);
            return nextDefaults;
        } catch (e) {
            setShippingDefaultsError(e.message || 'Failed to fetch shipping defaults');
            return null;
        } finally {
            setShippingDefaultsLoading(false);
        }
    }, [fetch, originCountry, productUuid, shippingDefaultsUrl]);

    const save = useCallback(async (fields) => {
        const current = fields ? { ...stateRef.current, ...fields } : stateRef.current;
        const previous = savedStateRef.current;
        const changedFields = SHIPPING_FIELDS.filter((field) => (
            Object.prototype.hasOwnProperty.call(current, field)
            && !valuesMatch(current[field], previous[field])
        ));

        if (changedFields.length === 0) {
            setError('');
            return true;
        }

        const payload = changedFields.reduce((acc, field) => {
            acc[field] = current[field];
            return acc;
        }, {});

        setSaving(true);
        setError('');

        try {
            const res = await fetch(`${productUrl}/shipping`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const json = await res.json();

            if (!res.ok) {
                if (json.message === 'No changes detected in shipping settings') {
                    return true;
                }
                throw new Error(json.error || json.message || 'Failed to save shipping settings');
            }

            const nextShipping = normalizeState(
                json.data?.shipping || current,
                originCountry,
            );
            setState(nextShipping);
            setSavedState(nextShipping);
            setIsFirstTime(!nextShipping.mode && !nextShipping.originZIPCode);

            if (changedFields.some((field) => EXAMPLE_REFRESH_FIELDS.has(field))) {
                await fetchExamples();
            }

            return true;
        } catch (e) {
            setError(e.message || 'Failed to save shipping settings');
            return false;
        } finally {
            setSaving(false);
        }
    }, [fetch, fetchExamples, originCountry, productUrl]);

    return {
        state,
        setField,
        setFields,
        save,
        saving,
        error,
        loading,
        examples,
        examplesLoading,
        examplesError,
        fetchExamples,
        shippingDefaults,
        shippingDefaultsLoading,
        shippingDefaultsError,
        fetchShippingDefaults,
        isFirstTime,
    };
}
